import { useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  updateURLWithLocation,
  updateURLWithDirections,
  updateURLWithStandaloneStop,
} from "Utils/tourNavigation";
import { IRootState } from "../../store/rootState";
import { useTrailFinder } from "./useTrailFinder";
import { useURLParser } from "./useURLParser";
import { useTrailChangeTracker } from "./useTrailChangeTracker";
import {
  CustomTrailStop,
  CustomTrail,
  TrailContainingStopResult,
} from "../../types/tour";

interface NavigationParams {
  currentTrailId: string | null;
  currentStop: number;
  trailChanged: boolean;
  findCurrentTrail: CustomTrail | null;
  findTrailContainingStop: (stopId: number) => TrailContainingStopResult | null;
  getCurrentLocFromURL: string | null;
  getValidatedPointIndex: (trail: CustomTrail | null, index: number) => number;
  suggestedTrails: CustomTrail[] | null;
}

interface NavigationResult {
  trailId: string | null;
  pointIndex: number;
  isStandalone?: boolean;
  stopId?: number;
}

// Helper function to extract point index from URL parameter
const extractPointIndexFromURL = (
  locParam: string | null,
  findCurrentTrail: CustomTrail | null,
  getValidatedPointIndex: (trail: CustomTrail | null, index: number) => number
): number => {
  if (!locParam) return 0;

  const locParts = locParam.split(":");
  if (locParts.length >= 2) {
    const existingPointIndex = parseInt(locParts[1], 10);
    return getValidatedPointIndex(findCurrentTrail, existingPointIndex);
  }
  return 0;
};

// Case 1: Trail changed but stop is cleared - preserve URL point index
const handleTrailChangedStopCleared = (
  params: NavigationParams
): NavigationResult | null => {
  const {
    trailChanged,
    currentStop,
    currentTrailId,
    findCurrentTrail,
    getCurrentLocFromURL,
    getValidatedPointIndex,
  } = params;

  if (trailChanged && currentStop === -1 && currentTrailId) {
    const pointIndex = extractPointIndexFromURL(
      getCurrentLocFromURL,
      findCurrentTrail,
      getValidatedPointIndex
    );
    return { trailId: currentTrailId, pointIndex };
  }
  return null;
};

// Case 2: Both trail and stop available - find stop index within trail
const handleTrailAndStopAvailable = (
  params: NavigationParams
): NavigationResult | null => {
  const {
    currentTrailId,
    currentStop,
    findCurrentTrail,
    findTrailContainingStop,
  } = params;

  if (currentTrailId && typeof currentStop === "number" && currentStop >= 0) {
    const currentTrail = findCurrentTrail;

    if (currentTrail?.Stops) {
      const stopIndex = currentTrail.Stops.findIndex(
        (stop: CustomTrailStop) => {
          const stopId = String(stop.stopid || stop.id);
          return stopId === String(currentStop);
        }
      );

      if (stopIndex !== -1) {
        return { trailId: currentTrailId, pointIndex: stopIndex };
      }
      // Stop not found in current trail, search other trails
      const result = findTrailContainingStop(currentStop);
      if (result) {
        return {
          trailId: String(result.trail.trailid || result.trail.id),
          pointIndex: result.index,
        };
      }
      // Stop is standalone - not in any trail
      return {
        trailId: null,
        pointIndex: 0,
        isStandalone: true,
        stopId: currentStop,
      };
    }
    // No stops available, use currentStop as point index
    return { trailId: currentTrailId, pointIndex: currentStop };
  }
  return null;
};

// Case 3: Trail only - preserve URL point index
const handleTrailOnly = (params: NavigationParams): NavigationResult | null => {
  const {
    currentTrailId,
    findCurrentTrail,
    getCurrentLocFromURL,
    getValidatedPointIndex,
  } = params;

  if (currentTrailId) {
    const pointIndex = extractPointIndexFromURL(
      getCurrentLocFromURL,
      findCurrentTrail,
      getValidatedPointIndex
    );
    return { trailId: currentTrailId, pointIndex };
  }
  return null;
};

// Case 4: Stop only - find containing trail or mark as standalone
const handleStopOnly = (params: NavigationParams): NavigationResult | null => {
  const { currentStop, suggestedTrails, findTrailContainingStop } = params;

  if (typeof currentStop === "number" && currentStop >= 0) {
    // Try to find the stop in trails if trails are available
    if (suggestedTrails?.length > 0) {
      const result = findTrailContainingStop(currentStop);
      if (result) {
        return {
          trailId: String(result.trail.trailid || result.trail.id),
          pointIndex: result.index,
        };
      }
    }
    // Stop is standalone - not in any trail
    return {
      trailId: null,
      pointIndex: 0,
      isStandalone: true,
      stopId: currentStop,
    };
  }
  return null;
};

// Main function - now much cleaner and testable
const determineNavigationParams = (
  params: NavigationParams
): NavigationResult => {
  // Try each case in priority order
  const result =
    handleTrailChangedStopCleared(params) ||
    handleTrailAndStopAvailable(params) ||
    handleTrailOnly(params) ||
    handleStopOnly(params);

  // Return result or fallback
  return result || { trailId: null, pointIndex: 0 };
};

export const useURLSync = () => {
  const currentStop = useSelector(
    (state: IRootState) => state.currentStop?.currentStop
  );
  const currentView = useSelector(
    (state: IRootState) => state.searchOptionsView?.view
  );
  const directionsState = useSelector((state: IRootState) => state.directions);

  const {
    findCurrentTrail,
    findTrailContainingStop,
    currentTrailId,
    suggestedTrails,
  } = useTrailFinder();
  const { getCurrentLocFromURL, getValidatedPointIndex } = useURLParser();
  const { hasTrailChanged } = useTrailChangeTracker(currentTrailId);

  // Debounce flag to prevent race conditions
  const isUpdatingRef = useRef(false);
  // Track timeout to prevent memory leaks
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup function for timeouts
  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Set debounce with cleanup
  const setDebounce = useCallback(
    (callback: () => void, delay: number) => {
      clearPendingTimeout();
      timeoutRef.current = setTimeout(callback, delay);
    },
    [clearPendingTimeout]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPendingTimeout();
    };
  }, [clearPendingTimeout]);

  // Memoize expensive computations to prevent unnecessary recalculations
  const trailChanged = useMemo(() => hasTrailChanged(), [hasTrailChanged]);

  // Memoize directions state IDs to prevent unnecessary re-renders
  const directionsIds = useMemo(
    () => ({
      startId: directionsState?.startingPoint?.id,
      endId: directionsState?.destination?.id,
    }),
    [directionsState?.startingPoint?.id, directionsState?.destination?.id]
  );

  // Memoize navigation parameters to prevent recalculation
  const navigationParams = useMemo(
    () => ({
      currentTrailId,
      currentStop,
      trailChanged,
      findCurrentTrail,
      findTrailContainingStop,
      getCurrentLocFromURL,
      getValidatedPointIndex,
      suggestedTrails,
    }),
    [
      currentTrailId,
      currentStop,
      trailChanged,
      findCurrentTrail,
      findTrailContainingStop,
      getCurrentLocFromURL,
      getValidatedPointIndex,
      suggestedTrails,
    ]
  );

  // Single consolidated effect for all URL sync operations
  useEffect(() => {
    if (isUpdatingRef.current) return;

    // Case 1: Directions view - handle directions URL sync
    if (
      currentView === "directions" &&
      directionsIds.startId &&
      directionsIds.endId
    ) {
      const startStopId = String(directionsIds.startId);
      const endStopId = String(directionsIds.endId);

      isUpdatingRef.current = true;
      updateURLWithDirections(startStopId, endStopId);
      setDebounce(() => {
        isUpdatingRef.current = false;
      }, 100);
      return; // Early return to prevent other cases
    }

    // Skip remaining cases for directions view
    if (currentView === "directions") return;

    // Case 2: View-only changes (trail changed but stop cleared)
    if (
      currentStop === -1 &&
      !navigationParams.trailChanged &&
      currentView &&
      currentTrailId
    ) {
      const locParam = navigationParams.getCurrentLocFromURL;
      if (locParam) {
        const parts = locParam.split(":");
        if (parts.length >= 2) {
          isUpdatingRef.current = true;
          updateURLWithLocation(
            currentTrailId,
            parseInt(parts[1], 10) || 0,
            currentView
          );
          setDebounce(() => {
            isUpdatingRef.current = false;
          }, 100);
          return; // Early return to prevent main navigation logic
        }
      }
    }

    // Case 3: Main navigation logic
    if (currentStop === -1 && !navigationParams.trailChanged) {
      return; // Skip if just a view change with cleared stop
    }

    const navigationResult = determineNavigationParams(navigationParams);

    if (
      navigationResult.isStandalone &&
      navigationResult.stopId !== undefined
    ) {
      // Handle standalone stop
      isUpdatingRef.current = true;
      updateURLWithStandaloneStop(navigationResult.stopId);
      setDebounce(() => {
        isUpdatingRef.current = false;
      }, 100);
    } else if (navigationResult.trailId) {
      // Handle trail-based stop
      isUpdatingRef.current = true;
      updateURLWithLocation(
        navigationResult.trailId,
        navigationResult.pointIndex,
        currentView
      );
      setDebounce(() => {
        isUpdatingRef.current = false;
      }, 100);
    }
  }, [
    // Core state that triggers URL updates
    currentView,
    currentStop,
    currentTrailId,
    // Memoized values to prevent unnecessary re-renders
    directionsIds.startId,
    directionsIds.endId,
    navigationParams,
    // Stable references
    setDebounce,
  ]);
};

export default useURLSync;
